import React, { useState, useEffect } from 'react';
import api from '../../lib/api';

// Types
interface LicenseDetails {
  id: string;
  planType: string;
  licenseType: string;
  expiresAt: string;
  isActive: boolean;
  daysUntilExpiry: number;
  isExpired: boolean;
  isExpiringSoon: boolean;
  planDetails: {
    name: string;
    monthlyPrice: number;
    maxUsers: number;
    features: Record<string, boolean>;
  };
  statusBadge: {
    color: string;
    text: string;
    icon: string;
  };
  renewalMessage: string;
}

interface Customer {
  id: string;
  name: string;
  status: string;
  deploymentType: string;
}

// 🏥 Customer License Status Page
const LicenseStatusPage: React.FC = () => {
  const [license, setLicense] = useState<LicenseDetails | null>(null);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [loading, setLoading] = useState(true);
  const [showRenewalModal, setShowRenewalModal] = useState(false);
  const [renewalRequests, setRenewalRequests] = useState([]);

  useEffect(() => {
    loadLicenseStatus();
    loadRenewalRequests();
  }, []);

  const loadLicenseStatus = async () => {
    try {
      // Get customer ID from auth context or local storage
      const customerId = localStorage.getItem('customerId') || 'demo-customer-id';
      const response = await api.get(`/customers/${customerId}/license`);
      
      setCustomer(response.data.customer);
      setLicense(response.data.license);
    } catch (error) {
      console.error('Failed to load license status:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadRenewalRequests = async () => {
    try {
      const customerId = localStorage.getItem('customerId') || 'demo-customer-id';
      const response = await api.get(`/customers/${customerId}/renewal-requests`);
      setRenewalRequests(response.data.renewalRequests);
    } catch (error) {
      console.error('Failed to load renewal requests:', error);
    }
  };

  const submitRenewalRequest = async (requestData: any) => {
    try {
      const customerId = localStorage.getItem('customerId') || 'demo-customer-id';
      await api.post(`/customers/${customerId}/renewal-request`, requestData);
      
      setShowRenewalModal(false);
      loadRenewalRequests();
      
      // Show success message
      alert('Renewal request submitted successfully!');
    } catch (error) {
      console.error('Failed to submit renewal request:', error);
      alert('Failed to submit renewal request. Please try again.');
    }
  };

  // 🚫 Expired License View
  const ExpiredLicenseView = () => (
    <div className="min-h-screen bg-red-50 flex items-center justify-center">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
        <div className="mb-6">
          <span className="text-6xl">❌</span>
          <h1 className="text-2xl font-bold text-gray-900 mt-4">License Expired</h1>
          <p className="text-gray-600 mt-2">
            Your HMS license has expired. Please request a renewal to continue using the system.
          </p>
        </div>

        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <div className="text-sm text-gray-600">
            <p><strong>Hospital:</strong> {customer?.name}</p>
            <p><strong>Plan:</strong> {license?.planDetails.name}</p>
            <p><strong>Expired:</strong> {license?.expiresAt && new Date(license.expiresAt).toLocaleDateString()}</p>
          </div>
        </div>

        <button
          onClick={() => setShowRenewalModal(true)}
          className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 font-medium"
        >
          🔄 Request License Renewal
        </button>

        <p className="text-xs text-gray-500 mt-4">
          Your data is safe and will be restored once your license is renewed.
        </p>
      </div>
    </div>
  );

  // 📋 License Status Dashboard
  const LicenseStatusDashboard = () => (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">License Status</h1>
          <p className="mt-2 text-gray-600">
            Manage your HMS license and subscription details
          </p>
        </div>

        {/* License Status Card */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-8">
          <div className="px-6 py-4 bg-gradient-to-r from-blue-500 to-purple-600">
            <div className="flex items-center justify-between text-white">
              <div>
                <h2 className="text-xl font-bold">{customer?.name}</h2>
                <p className="text-blue-100">{customer?.deploymentType} Deployment</p>
              </div>
              <div className="text-right">
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                  license?.statusBadge.color === 'green' ? 'bg-green-100 text-green-800' :
                  license?.statusBadge.color === 'yellow' ? 'bg-yellow-100 text-yellow-800' :
                  license?.statusBadge.color === 'red' ? 'bg-red-100 text-red-800' :
                  'bg-blue-100 text-blue-800'
                }`}>
                  {license?.statusBadge.icon} {license?.statusBadge.text}
                </span>
              </div>
            </div>
          </div>

          <div className="px-6 py-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              
              {/* Plan Details */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Current Plan</h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Plan:</span>
                    <span className="font-medium">{license?.planDetails.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Type:</span>
                    <span className="font-medium">{license?.licenseType}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Max Users:</span>
                    <span className="font-medium">{license?.planDetails.maxUsers}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Monthly Price:</span>
                    <span className="font-medium">${license?.planDetails.monthlyPrice}</span>
                  </div>
                </div>
              </div>

              {/* Expiry Information */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Expiry Details</h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Expires On:</span>
                    <span className="font-medium">
                      {license?.expiresAt && new Date(license.expiresAt).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Days Remaining:</span>
                    <span className={`font-medium ${
                      (license?.daysUntilExpiry || 0) <= 7 ? 'text-red-600' :
                      (license?.daysUntilExpiry || 0) <= 30 ? 'text-yellow-600' : 'text-green-600'
                    }`}>
                      {license?.daysUntilExpiry} days
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Auto Renew:</span>
                    <span className="font-medium">
                      {license?.isActive ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Actions</h3>
                <div className="space-y-3">
                  {license?.isExpiringSoon && (
                    <button
                      onClick={() => setShowRenewalModal(true)}
                      className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 font-medium"
                    >
                      🔄 Renew License
                    </button>
                  )}
                  
                  <button
                    onClick={() => setShowRenewalModal(true)}
                    className="w-full bg-gray-600 text-white py-2 px-4 rounded-lg hover:bg-gray-700 font-medium"
                  >
                    📈 Upgrade Plan
                  </button>
                  
                  <button className="w-full border border-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-50 font-medium">
                    📄 Download Invoice
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Renewal Message */}
        {license?.renewalMessage && (
          <div className={`rounded-lg p-4 mb-8 ${
            license.isExpired ? 'bg-red-50 border border-red-200' :
            license.isExpiringSoon ? 'bg-yellow-50 border border-yellow-200' :
            'bg-green-50 border border-green-200'
          }`}>
            <p className={`${
              license.isExpired ? 'text-red-800' :
              license.isExpiringSoon ? 'text-yellow-800' :
              'text-green-800'
            }`}>
              {license.renewalMessage}
            </p>
          </div>
        )}

        {/* Features List */}
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Plan Features</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {license?.planDetails.features && Object.entries(license.planDetails.features).map(([feature, enabled]) => (
              <div key={feature} className="flex items-center">
                <span className={`mr-2 ${enabled ? 'text-green-500' : 'text-gray-400'}`}>
                  {enabled ? '✅' : '❌'}
                </span>
                <span className={`capitalize ${enabled ? 'text-gray-900' : 'text-gray-500'}`}>
                  {feature.replace(/([A-Z])/g, ' $1').trim()}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Renewal Requests History */}
        {renewalRequests.length > 0 && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Renewal Requests</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Request Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Requested Plan
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {renewalRequests.map((request: any) => (
                    <tr key={request.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {request.requestType}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {request.requestedPlan}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded ${
                          request.status === 'APPROVED' ? 'bg-green-100 text-green-800' :
                          request.status === 'REJECTED' ? 'bg-red-100 text-red-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {request.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(request.createdAt).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // 🔄 Renewal Request Modal
  const RenewalRequestModal = () => {
    const [requestType, setRequestType] = useState('EXTENSION');
    const [requestedPlan, setRequestedPlan] = useState(license?.planType || 'STANDARD');
    const [message, setMessage] = useState('');
    const [urgency, setUrgency] = useState('MEDIUM');

    const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      submitRenewalRequest({
        requestType,
        requestedPlan,
        requestedDuration: 30,
        message,
        urgency
      });
    };

    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-lg max-w-md w-full mx-4">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Request License Renewal</h3>
          </div>
          
          <form onSubmit={handleSubmit} className="px-6 py-4">
            <div className="space-y-4">
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Request Type
                </label>
                <select 
                  value={requestType}
                  onChange={(e) => setRequestType(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                >
                  <option value="EXTENSION">Extend Current Plan</option>
                  <option value="UPGRADE">Upgrade Plan</option>
                  <option value="REACTIVATION">Reactivate License</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Requested Plan
                </label>
                <select 
                  value={requestedPlan}
                  onChange={(e) => setRequestedPlan(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                >
                  <option value="STANDARD">Standard ($99/month)</option>
                  <option value="PREMIUM">Premium ($199/month)</option>
                  <option value="ENTERPRISE">Enterprise ($499/month)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Urgency
                </label>
                <select 
                  value={urgency}
                  onChange={(e) => setUrgency(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                >
                  <option value="LOW">Low</option>
                  <option value="MEDIUM">Medium</option>
                  <option value="HIGH">High</option>
                  <option value="CRITICAL">Critical</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Message (Optional)
                </label>
                <textarea 
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={3}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  placeholder="Additional information about your renewal request..."
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button 
                type="button"
                onClick={() => setShowRenewalModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button 
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Submit Request
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading license information...</p>
        </div>
      </div>
    );
  }

  // Show expired view if license is expired
  if (license?.isExpired) {
    return (
      <>
        <ExpiredLicenseView />
        {showRenewalModal && <RenewalRequestModal />}
      </>
    );
  }

  return (
    <>
      <LicenseStatusDashboard />
      {showRenewalModal && <RenewalRequestModal />}
    </>
  );
};

export default LicenseStatusPage; 